import MarketingLayout from '../layouts/MarketingLayout.jsx'
import SEOHead from '../components/SEOHead.jsx'
import CTAButton from '../components/CTAButton.jsx'
import PlaceholderImage from '../components/PlaceholderImage.jsx'
import imgCreateQuiz   from '../images/kartquiz-cree-ton-quiz.webp'
import imgAI           from '../images/quiz-genere-par-ia.webp'
import imgKahoot       from '../images/quiz-importer-kahoot-course.webp'
import imgGrandEcran   from '../images/kartquiz-sur-grand-ecran.webp'
import imgBonusJoueur  from '../images/kartquiz-bonus-joueur.webp'
import imgPilotage     from '../images/kartquiz-course-pilotage.webp'
import imgSynthese     from '../images/quiz-synthese-des-revisions.webp'
import imgSansApp      from '../images/kartquiz-onboarding-sans-app.webp'
import imgRoadmap      from '../images/kartquiz-roadmap-eleve.webp'

const pageJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Toutes les fonctionnalités de KartQuiz',
  description:
    'Création de quiz, génération par IA, circuit de course, bonus, statistiques live, synthèse pédagogique — tout ce que KartQuiz peut faire pour ta classe.',
  url: 'https://kartquiz.fr/fonctionnalites',
}

const BONUS_LIST = [
  { emoji: '🍌', name: 'Banane', freq: 'Fréquent', effet: 'Dépose un piège sur ta case — le prochain joueur qui s\'y arrête recule d\'une case.' },
  { emoji: '🐚', name: 'Coquille', freq: 'Fréquent', effet: 'Réduit de 50 % le timer du joueur juste devant toi sur la prochaine question.' },
  { emoji: '💣', name: 'Bombe', freq: 'Rare', effet: 'Fait reculer de 3 cases le joueur devant et tous ceux sur la même case.' },
  { emoji: '🪝', name: 'Crochet', freq: 'Fréquent', effet: 'Tire le 1er de la course 3 cases en arrière. Le lanceur avance de 1 case.' },
  { emoji: '🚀', name: 'Turbo', freq: 'Fréquent', effet: 'Propulse immédiatement ton kart de 2 cases en avant.' },
  { emoji: '🛡', name: 'Bouclier', freq: 'Moyen', effet: 'Absorbe la prochaine attaque reçue (jusqu\'à 30 s).' },
  { emoji: '⭐', name: 'Étoile', freq: 'Rare', effet: 'Invincibilité totale pendant 60 s — aucune attaque ne peut te toucher.' },
  { emoji: '❄️', name: 'Gel', freq: 'Rare', effet: 'Gèle tous les autres joueurs pendant 12 s — leurs boutons sont bloqués.' },
]

function SectionHeader({ eyebrow, title, lead }) {
  return (
    <div className="text-center mb-16 max-w-2xl mx-auto">
      {eyebrow && (
        <p className="text-kart-action font-semibold text-sm uppercase tracking-widest mb-3">{eyebrow}</p>
      )}
      <h2 className="font-display font-semibold text-3xl md:text-4xl text-kart-night mb-4">{title}</h2>
      {lead && <p className="text-slate-500 text-lg leading-relaxed">{lead}</p>}
    </div>
  )
}

function FeatureRow({ title, body, image, imageSrc, imageAlt, reverse = false, tag }) {
  return (
    <div className={`flex flex-col ${reverse ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-12 mb-24`}>
      <div className="flex-1 space-y-4">
        {tag && (
          <span className="inline-block text-xs font-semibold uppercase tracking-widest text-kart-teal bg-kart-teal/10 px-3 py-1 rounded-full">
            {tag}
          </span>
        )}
        <h3 className="font-display font-semibold text-2xl text-kart-night">{title}</h3>
        <div className="text-slate-600 leading-relaxed space-y-3">{body}</div>
      </div>
      <div className="flex-1 w-full">
        {imageSrc ? (
          <img src={imageSrc} alt={imageAlt} loading="lazy" decoding="async" className="w-full" />
        ) : (
          <PlaceholderImage filename={image} alt={imageAlt} width={1000} height={700} className="w-full shadow-lg" />
        )}
      </div>
    </div>
  )
}

function CTASection() {
  return (
    <section className="bg-kart-night py-20">
      <div className="max-w-3xl mx-auto px-6 text-center space-y-6">
        <h2 className="font-display font-semibold text-3xl md:text-4xl text-white">
          Prêt à mettre ta classe sur la grille de départ ?
        </h2>
        <p className="text-slate-300 text-lg">
          Créer ton compte prend 30 secondes. La première course peut démarrer dans la foulée.
        </p>
        <CTAButton to="/login" size="lg">Créer un compte gratuit</CTAButton>
        <p className="text-slate-500 text-sm">Sans carte bancaire · 100 % gratuit pendant la bêta</p>
      </div>
    </section>
  )
}

export default function FeaturesPage() {
  return (
    <MarketingLayout>
      <SEOHead
        title="Toutes les fonctionnalités de KartQuiz | KartQuiz"
        description="Création de quiz, IA, circuit de course, bonus d'attaque, synthèse pédagogique — tout ce que KartQuiz peut faire pour ta classe."
        canonical="https://kartquiz.fr/fonctionnalites"
        jsonLd={[pageJsonLd]}
      />

      {/* Hero */}
      <section className="bg-[#F8FAFC] py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-6 text-center space-y-6">
          <p className="text-kart-action font-semibold text-sm uppercase tracking-widest">Fonctionnalités</p>
          <h1 className="font-display font-semibold text-4xl md:text-5xl text-kart-night">
            Toutes les fonctionnalités de KartQuiz
          </h1>
          <p className="text-slate-500 text-xl leading-relaxed">
            Tout ce qu'il faut pour transformer une révision en course palpitante — sans complexité.
          </p>
          <CTAButton to="/login" size="lg">Créer un compte gratuit</CTAButton>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">

          <FeatureRow
            tag="Création de quiz"
            title="Crée ton quiz en 4 façons différentes"
            body={
              <>
                <p>
                  <strong>À la main</strong> — l'éditeur de questions est simple et rapide. Tu ajoutes un énoncé, 4 réponses, tu coches la bonne, tu valides. Pas besoin de formation.
                </p>
                <p>
                  <strong>Par l'IA</strong> — tape ton sujet en langage naturel (*« 15 questions sur la Révolution française niveau 4ème »*) et le quiz est généré en 30 secondes. Tu relis, tu ajustes si besoin, tu lances.
                </p>
                <p>
                  <strong>Depuis Kahoot</strong> — colle l'URL d'un quiz public Kahoot et toutes ses questions arrivent dans KartQuiz en quelques secondes. Zéro ressaisie.
                </p>
                <p>
                  <strong>Depuis la bibliothèque</strong> — retrouve les quiz partagés par la communauté, filtre par matière et niveau, et adapte à ta classe.
                </p>
              </>
            }
            imageSrc={imgCreateQuiz}
            imageAlt="Interface de création de quiz KartQuiz"
          />

          <FeatureRow
            tag="Intelligence artificielle"
            title="L'IA génère tes quiz à ta place"
            body={
              <>
                <p>
                  Fini les soirées à taper des QCM. Donne un sujet, un niveau et le nombre de questions. L'IA produit un quiz complet avec des réponses plausibles et des distracteurs pertinents.
                </p>
                <p>
                  Tu gardes la main à 100 % : chaque question est modifiable avant de lancer la partie. L'IA suggère, toi tu décides.
                </p>
                <ul className="list-disc list-inside text-sm space-y-1.5 mt-2">
                  <li>Génération en français, en anglais, ou en langue au choix</li>
                  <li>Adaptatif au niveau (6ème → terminale → supérieur)</li>
                  <li>Inclus dans la bêta gratuite, sans limite</li>
                </ul>
              </>
            }
            imageSrc={imgAI}
            imageAlt="Génération de quiz par IA dans KartQuiz"
            reverse
          />

          <FeatureRow
            tag="Import Kahoot"
            title="Tes quiz Kahoot deviennent des courses de kart"
            body={
              <>
                <p>
                  Tu as déjà investi du temps sur Kahoot ? Aucune raison de tout recommencer. Colle l'URL d'un quiz Kahoot public dans KartQuiz — toutes les questions QCM à 4 réponses sont importées en quelques secondes, prêtes à jouer.
                </p>
                <p>
                  La même révision, mais avec les bonus, les attaques, la vitesse adaptative et la synthèse pédagogique que Kahoot n'a pas.
                </p>
                <ul className="list-disc list-inside text-sm space-y-1.5 mt-2">
                  <li>Fonctionne avec n'importe quel quiz public sur Kahoot.it</li>
                  <li>Les questions à 4 réponses sont importées automatiquement</li>
                  <li>Tu peux modifier, réorganiser ou supprimer des questions après import</li>
                  <li>Compatible avec les quiz publics de la communauté Kahoot (des millions de quiz)</li>
                </ul>
              </>
            }
            imageSrc={imgKahoot}
            imageAlt="Import de quiz Kahoot dans KartQuiz"
            reverse
          />

          <FeatureRow
            tag="Circuit de course"
            title="Un circuit cartoon, vu de dessus, en temps réel"
            body={
              <>
                <p>
                  Chaque partie se joue sur un circuit oval style Mario Kart. Les karts des joueurs avancent en temps réel à chaque bonne réponse — visible sur les téléphones des élèves et sur le grand écran affiché en classe.
                </p>
                <p>
                  Les thèmes visuels de circuit sont personnalisables : chaque thème a une image de fond différente (espace, sous-marin, forêt, ville...).
                </p>
                <ul className="list-disc list-inside text-sm space-y-1.5 mt-2">
                  <li>Circuit SVG ovale, vue de dessus, style cartoon adulte</li>
                  <li>Cases étoile (bonus) et ligne d'arrivée clairement indiquées</li>
                  <li>Groupement des karts si plusieurs joueurs à la même case</li>
                  <li>Déplacement animé fluide (interpolation sur le tracé)</li>
                </ul>
              </>
            }
            imageSrc={imgGrandEcran}
            imageAlt="Circuit de course KartQuiz vue de dessus"
          />
        </div>
      </section>

      {/* Bonus */}
      <section className="py-20 md:py-28 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeader
            eyebrow="Bonus & attaques"
            title="8 bonus qui rebattent les cartes"
            lead="Les bonus sont collectés sur les cases spéciales du circuit. Chaque joueur peut en stocker un à la fois — et choisit le moment pour l'utiliser."
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
            {BONUS_LIST.map(b => (
              <div key={b.name} className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <span className="text-4xl">{b.emoji}</span>
                  <span className={`text-xs font-semibold uppercase tracking-wide px-2 py-1 rounded-full ${
                    b.freq === 'Fréquent' ? 'bg-kart-teal/10 text-kart-teal' :
                    b.freq === 'Rare'     ? 'bg-kart-action/10 text-kart-action' :
                    'bg-slate-100 text-slate-500'
                  }`}>
                    {b.freq}
                  </span>
                </div>
                <h3 className="font-display font-semibold text-kart-night">{b.name}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{b.effet}</p>
              </div>
            ))}
          </div>

          <div className="flex justify-center">
            <img src={imgBonusJoueur} alt="Vue des bonus en action dans KartQuiz" loading="lazy" decoding="async" className="max-w-sm w-full" />
          </div>
        </div>
      </section>

      {/* Player + Host experience */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">

          <FeatureRow
            tag="Expérience joueur"
            title="Tout le jeu dans le navigateur, sans app"
            body={
              <>
                <p>
                  Tes élèves rejoignent la partie en flashant un QR code ou en tapant un code à 6 caractères sur leur téléphone. Aucune inscription, aucune application à télécharger.
                </p>
                <p>
                  L'interface mobile est épurée : mini-carte du circuit, timer visible, 4 boutons de réponse bien espacés. Conçue pour fonctionner sur des téléphones de 3 ans minimum.
                </p>
                <ul className="list-disc list-inside text-sm space-y-1.5 mt-2">
                  <li>Compatible iOS Safari 14+ et Android Chrome 90+</li>
                  <li>Timer min (empêche les clics au hasard) + timer max (deadline)</li>
                  <li>Bandeau vitesse (V1 🔵 / V2 🟠 / V3 🔴) et position en temps réel</li>
                  <li>Synthèse personnalisée en fin de partie</li>
                </ul>
              </>
            }
            imageSrc={imgSansApp}
            imageAlt="Vue téléphone joueur KartQuiz"
          />

          <FeatureRow
            tag="Expérience hôte"
            title="Tu pilotes depuis le grand écran"
            body={
              <>
                <p>
                  En tant qu'enseignant, tu vois la partie en temps réel sur un grand écran (vidéoprojecteur ou TV). Circuit plein écran, karts animés, classement glissant en bas — tes élèves voient exactement la même chose sur leur téléphone.
                </p>
                <p>
                  Tu gères la partie depuis l'interface hôte : démarrage, pause, fin de partie.
                </p>
                <ul className="list-disc list-inside text-sm space-y-1.5 mt-2">
                  <li>Vue circuit grand écran avec emojis joueurs animés</li>
                  <li>Bandeau classement animé (transitions fluides)</li>
                  <li>Vue spectateur accessible depuis n'importe quel appareil via le code</li>
                </ul>
              </>
            }
            imageSrc={imgPilotage}
            imageAlt="Vue hôte grand écran KartQuiz"
            reverse
          />
        </div>
      </section>

      {/* Stats + synthesis */}
      <section className="py-20 md:py-28 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6">

          <FeatureRow
            tag="Statistiques live"
            title="Vois l'engagement en temps réel"
            body={
              <>
                <p>
                  Pendant la partie, le taux de bonnes réponses est calculé en direct par sous-thème. Tu identifies immédiatement les notions qui coincent pour toute la classe.
                </p>
                <p>
                  En fin de partie, les statistiques sont disponibles sous forme de jauges arc par sous-thème : bonnes réponses, mauvaises réponses, timeouts — drill-down jusqu'à la distribution A/B/C/D pour chaque question.
                </p>
              </>
            }
            imageSrc={imgSynthese}
            imageAlt="Statistiques live par sous-thème dans KartQuiz"
          />

          <FeatureRow
            tag="Synthèse pédagogique"
            title="Chaque élève repart avec sa feuille de route"
            body={
              <>
                <p>
                  À la fin de la course, chaque joueur reçoit sa synthèse personnalisée : les notions <strong>maîtrisées</strong>, celles <strong>acquises après correction</strong>, et celles <strong>à réviser en priorité</strong>.
                </p>
                <p>
                  Toi, tu as la vue d'ensemble : les notions qui coincent pour toute la classe, identifiées par sous-thème et par question.
                </p>
                <ul className="list-disc list-inside text-sm space-y-1.5 mt-2">
                  <li>Synthèse joueur : Maîtrisé / Acquis après correction / À réviser</li>
                  <li>Vue classe : taux par sous-thème, distribution par question</li>
                  <li>Podium animé top 3 avant les statistiques</li>
                </ul>
              </>
            }
            imageSrc={imgRoadmap}
            imageAlt="Synthèse pédagogique de fin de partie KartQuiz"
            reverse
          />

          <FeatureRow
            tag="Bibliothèque communauté"
            title="Profite des quiz créés par la communauté"
            body={
              <>
                <p>
                  Les enseignants qui utilisent KartQuiz peuvent partager leurs quiz avec la communauté. Tu retrouves les quiz filtrés par matière, niveau et sous-thème — et tu peux les utiliser tels quels ou les adapter à ta classe.
                </p>
                <p className="text-kart-action font-medium text-sm">
                  🚧 La bibliothèque communauté est en cours de développement — disponible prochainement.
                </p>
              </>
            }
            image="feat-library.png"
            imageAlt="Bibliothèque communauté de quiz KartQuiz"
          />
        </div>
      </section>

      <CTASection />
    </MarketingLayout>
  )
}
